import { Auth } from "aws-amplify";
import { useCallback, useState, useEffect } from 'react';
import { HiOutlineEye, HiOutlineEyeOff, HiCheckCircle } from 'react-icons/hi';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../../assets/images/logo/logo.png';
import glogo from '../../../assets/images/icons/g-logo.png';
import authSideImage from '../../../assets/images/others/auth-side.webp';
import Loader from "../../../components/form-loader/form-loader";
import AuthTextBox from "../../../components/forms/auth-text-box";
import Message from "../../../components/message/message-comp";
import config from "../../../config";
import { getGoogleSignInUrl } from "../../../services/auth/auth-service";
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const validatePhonePattern = (phone) => {
  if (!phone) return false;
  try {
    const phoneNumber = parsePhoneNumberFromString(phone);
    return phoneNumber ? phoneNumber.isValid() : false;
  } catch (error) {
    return false;
  }
}

function SignupPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [imagePreloaded, setImagePreloaded] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(null);

  const [auth, setAuth] = useState({
    username: '',
    password: '',
    firstname: '',
    lastname: '',
    phoneNumber: '',
    organization: '',
    website: '',
    rememberMe: false
  });

  const [error, setError] = useState({
    username: false,
    firstname: false,
    phoneNumber: false,
    password: false
  });

  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    message: ''
  });

  // Pre-load image for smoother rendering
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = authSideImage;
    document.head.appendChild(link);

    const img = new Image();
    img.src = authSideImage;
    img.onload = () => {
      setImagePreloaded(true);
    };
    img.onerror = () => {
      setImageFailed(true);
    };

    return () => {
      try {
        document.head.removeChild(link);
      } catch (e) {
        // ignore
      }
    };
  }, []);

  const handleChange = useCallback(event => {
    const targetName = event.target.name;
    auth[targetName] = event.target.value;

    if (targetName === 'phoneNumber') {
      setIsPhoneValid(null);
    }

    if (triedSubmit) {
      validateForm()
    }

    setAuth(Object.assign({}, auth));
  });

  const handlePhoneBlur = () => {
    const trimmedPhone = auth.phoneNumber ? auth.phoneNumber.trim() : '';
    if (trimmedPhone) {
      const parsed = parsePhoneNumberFromString(trimmedPhone);
      const isValid = parsed ? parsed.isValid() : false;
      setIsPhoneValid(isValid);
      if (isValid) {
        auth.phoneNumber = parsed.number;
        setAuth(Object.assign({}, auth));
      }
      error.phoneNumber = !isValid;
      setError(Object.assign({}, error));
    } else {
      setIsPhoneValid(null);
      error.phoneNumber = false;
      setError(Object.assign({}, error));
    }
  };

  const validateForm = () => {
    let valid = true;
    if (!auth.username) {
      error.username = true;
      valid = false;
    } else {
      error.username = false;
    }

    if (!auth.password) {
      error.password = true;
      valid = false;
    } else {
      error.password = false;
    }

    if (!auth.firstname) {
      error.firstname = true;
      valid = false;
    } else {
      error.firstname = false;
    }

    const trimmedPhone = auth.phoneNumber ? auth.phoneNumber.trim() : '';
    if (trimmedPhone && !validatePhonePattern(trimmedPhone)) {
      error.phoneNumber = true;
      valid = false;
    } else {
      error.phoneNumber = false;
    }

    setError(Object.assign({}, error))
    return valid;
  }

  const updateFormState = (processing, success, failed, message) => {
    setFormState(Object.assign({}, { processing, success, failed, message }))
  }

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setTriedSubmit(true);
    if (!formState.processing && validateForm()) {
      updateFormState(true, false, false);
      try {
        const attributes = {
          'given_name': auth.firstname,
          'family_name': auth.lastname || auth.firstname,
          'name': auth.firstname + " " + auth.lastname,
          'custom:organization': auth.organization,
          'custom:website': auth.website,
        };
        const trimmedPhone = auth.phoneNumber ? auth.phoneNumber.trim() : '';
        if (trimmedPhone) {
          const parsed = parsePhoneNumberFromString(trimmedPhone);
          attributes['phone_number'] = (parsed && parsed.isValid()) ? parsed.number : trimmedPhone;
        }

        // alias pool: email is not the username, so sign up under a generated id
        if (config.COGNITO_EMAIL_AS_ALIAS) {
          attributes['email'] = auth.username;
        }
        await Auth.signUp({
          username: config.COGNITO_EMAIL_AS_ALIAS ? crypto.randomUUID() : auth.username,
          password: auth.password,
          attributes
        });
        updateFormState(false, true, false);
        navigate(`/signin?sign_up=success&account=${auth.username}`);
      } catch (err) {
        console.error("signup failed cause", err);
        updateFormState(
          false,
          false,
          true,
          err.message.includes("User not invited") ? "We regret to inform you that your account has not been enabled yet. Please do not hesitate to contact our team for further assistance." : err.message);
      }
    }
  });

  const onGoogleLogin = (event) => {
    event.preventDefault();
    window.location.href = getGoogleSignInUrl();
  }

  if (config.SELF_SIGNUP_DISABLED) {
    window.location.href = `/signin`;
    return null;
  }

  return (
    <div className="min-h-screen lg:h-screen grid grid-cols-1 lg:grid-cols-2 bg-white font-sans lg:overflow-hidden">
      <div className="flex flex-col justify-between p-6 sm:p-12 lg:p-16 overflow-y-auto h-screen lg:h-full">
        <div className="flex items-center">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              className="h-8 w-auto object-contain"
              src={logo}
              alt="BotCircuits"
            />
            <span className="text-xl font-bold text-gray-900 tracking-tight">BotCircuits</span>
          </Link>
        </div>

        <div className="my-auto mx-auto w-full max-w-md py-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Create your account</h2>
            <p className="mt-2 text-sm text-gray-600">Reliable AI for Financial Services that Runs Customer Operations</p>
          </div>

          {formState.processing && (
            <div className="mt-4 flex justify-center items-center">
              <Loader />
            </div>
          )}

          {formState.failed && (
            <div className="mt-4 flex justify-center items-center">
              <Message text={formState.message ? formState.message : 'Your details are incorrect and the signup was not successful. Please check them, and try again.'} type="error" />
            </div>
          )}

          <div className="mt-6">
            <form className="space-y-4" onSubmit={handleSubmit} method="POST">
              <button
                onClick={onGoogleLogin}
                type="button"
                className="flex w-full justify-center items-center rounded-lg border border-slate-200 bg-white py-2.5 px-4 text-sm font-semibold text-gray-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-lime focus:ring-offset-2 transition-all duration-200"
              >
                <img src={glogo} alt="Google" className="h-5 w-5 mr-3" />
                Sign up with Google
              </button>

              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-4 text-xs font-semibold text-gray-400 tracking-wider uppercase">OR</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              <AuthTextBox
                type="email"
                name="username"
                id="username"
                label="Work email"
                value={auth.username}
                placeholder="you@company.com"
                onChange={handleChange}
                error={error.username}
              />

              <AuthTextBox
                type="text"
                name="firstname"
                id="firstname"
                label="Name"
                value={auth.firstname}
                placeholder="Enter your name"
                onChange={handleChange}
                error={error.firstname}
              />

              <AuthTextBox
                type="text"
                name="phoneNumber"
                id="phoneNumber"
                label="Phone Number"
                value={auth.phoneNumber}
                placeholder="Enter your phone number (e.g. +1 202-555-0143)"
                onChange={handleChange}
                onBlur={handlePhoneBlur}
                suffix={isPhoneValid ? <HiCheckCircle className="text-green-500 w-5 h-5" /> : null}
                error={error.phoneNumber}
                errorMessage="Enter a valid international phone number (e.g. +1 202-555-0143)"
                optional={true}
              />

              <AuthTextBox
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                label="Password"
                value={auth.password}
                placeholder="Enter your password"
                onChange={handleChange}
                error={error.password}
                suffix={
                  showPassword ? (
                    <HiOutlineEye size={20} className="text-gray-500 hover:text-gray-700" onClick={() => setShowPassword(false)} />
                  ) : (
                    <HiOutlineEyeOff size={20} className="text-gray-500 hover:text-gray-700" onClick={() => setShowPassword(true)} />
                  )
                }
              />

              <div className="pt-2">
                <button
                  type="submit"
                  className="flex w-full justify-center items-center rounded-lg bg-primary py-3 px-4 text-sm font-semibold text-white shadow-md hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-lime transition-all duration-200 uppercase tracking-wider"
                >
                  Create account &rarr;
                </button>
              </div>

              <div className="text-xs text-gray-500 pt-2 leading-relaxed text-center lg:text-left">
                By signing up, you agree to our{' '}
                <a href="https://botcircuits.ai/privacypolicy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
                  Privacy Policy
                </a>.
              </div>

              <div className="text-sm pt-4 text-center lg:text-left">
                Already have an account?{' '}
                <Link to="/signin" className="font-semibold text-primary hover:text-gray-700 underline">
                  Sign in
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="text-xs text-gray-400 text-center lg:text-left space-x-2">
          <a href="https://botcircuits.ai/privacypolicy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 underline">
            Privacy Policy
          </a>
        </div>
      </div>

      <div className="hidden lg:flex bg-[#FAFAF9] relative items-center justify-center lg:h-full overflow-hidden select-none">
        {!imageFailed ? (
          <img
            src={authSideImage}
            alt="Customer operations that act, not just answer."
            className="w-full h-full object-contain object-left transition-opacity duration-300"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center space-y-4">
            <img src={logo} alt="BotCircuits" className="h-16 w-auto" />
            <span className="text-3xl font-extrabold text-gray-900 tracking-widest uppercase">BotCircuits</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignupPage;
